import { Input, InputNumber, Row, Select } from 'antd';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import DefaultFormItem from 'components/composites/DefaultFormItem';
import FieldValue from 'components/forms/FieldValue';
import FormItemCol from 'components/forms/FormItemCol';
import { loadBackOfficeEnterpriseSites } from 'store/modules/backOfficeEnterpriseSites/actions';
import useBackOfficeEnterpriseSites from 'store/modules/backOfficeEnterpriseSites/hooks';
import { FILTER_NULL } from 'store/modules/routerUtils/selectors';
import { SearchTypes } from 'store/modules/search/types';
import { IPCameraIntegrationType } from 'types/dto/ip-camera.dto';
import { EnterpriseId } from 'types/models/enterprise';
import useSearch from 'utils/use-search';

const INTEGRATION_TYPE_OPTIONS: { label: string; value: IPCameraIntegrationType }[] = [
  { label: 'Land Watch', value: 'landwatch' },
  { label: 'Dolynk', value: 'dolynk' }
];

const CATEGORY_CODE_OPTIONS = [
  { label: 'IPC - IP Camera', value: 'IPC' },
  { label: 'SD - Storage Device', value: 'SD' },
  { label: 'TPC - PTZ Camera', value: 'TPC' },
  { label: 'ITC - Intelligent Traffic Camera', value: 'ITC' },
  { label: 'NVR - Network Video Recorder', value: 'NVR' }
];

function IPCameraAddFormItems(): JSX.Element {
  const dispatch = useDispatch();

  const [enterpriseId, setEnterpriseId] = useState<EnterpriseId | undefined | null>();
  const [enterpriseSearchTerm, setEnterpriseSearchTerm] = useState<string | undefined>();

  const sites = useBackOfficeEnterpriseSites();

  const enterpriseSearchResults = useSearch(
    SearchTypes.UserEnterpriseMembershipSearch,
    enterpriseSearchTerm
  );

  const enterpriseOptions = useMemo(
    () => [
      ...(enterpriseSearchTerm
        ? []
        : [{ label: 'No Enterprise', value: FILTER_NULL }]),
      ...(enterpriseSearchResults || []).map(({ name, id }) => ({
        label: name,
        value: id.toString()
      }))
    ],
    [enterpriseSearchTerm, enterpriseSearchResults]
  );

  const siteOptions = useMemo(
    () => sites.map((site) => ({
      label: site.name,
      value: site.id
    })),
    [sites]
  );

  // Fetch sites when enterprise changes
  useEffect(() => {
    if (enterpriseId) {
      dispatch(loadBackOfficeEnterpriseSites(enterpriseId));
    }
  }, [enterpriseId, dispatch]);

  return (
    <>
      <Row gutter={24}>
        <FormItemCol>
          <DefaultFormItem
            name="integrationType"
            label="Integration Type"
            rules={[
              {
                required: true,
                message: 'Please select an integration type'
              }
            ]}
          >
            <Select
              options={INTEGRATION_TYPE_OPTIONS}
              placeholder="Select integration type"
            />
          </DefaultFormItem>
        </FormItemCol>

        <FormItemCol>
          <DefaultFormItem
            name="enterpriseId"
            label="Enterprise"
            rules={[
              {
                required: true,
                message: 'Please select an enterprise'
              }
            ]}
          >
            <Select
              id="enterprise-select"
              filterOption={false}
              showSearch
              onFocus={() => setEnterpriseSearchTerm(undefined)}
              onBlur={() => setEnterpriseSearchTerm(undefined)}
              onSearch={setEnterpriseSearchTerm}
              options={enterpriseOptions}
              placeholder="Enterprise"
              onSelect={(value) => {
                if (value === FILTER_NULL) {
                  setEnterpriseId(null);
                } else {
                  setEnterpriseId(Number(value));
                }
                setEnterpriseSearchTerm(undefined);
              }}
            />
          </DefaultFormItem>
        </FormItemCol>

        <FormItemCol>
          <DefaultFormItem
            name="siteId"
            label="Site"
            rules={[
              {
                required: true,
                message: 'Please select a site'
              }
            ]}
          >
            <Select
              id="site-select"
              options={siteOptions}
              disabled={!enterpriseId}
              placeholder="Site"
            />
          </DefaultFormItem>
        </FormItemCol>

        <FormItemCol>
          <DefaultFormItem
            name="name"
            label="IP Camera Name"
            rules={[
              {
                required: true,
                message: 'Please enter an IP camera name'
              }
            ]}
          >
            <Input placeholder="Name" />
          </DefaultFormItem>
        </FormItemCol>

        <FieldValue name="integrationType">
          {(integrationType: IPCameraIntegrationType) => integrationType === 'dolynk' && (
            <FormItemCol>
              <DefaultFormItem name="categoryCode" label="Category Code">
                <Select
                  options={CATEGORY_CODE_OPTIONS}
                  placeholder="Select category code"
                />
              </DefaultFormItem>
            </FormItemCol>
          )}
        </FieldValue>

        <FieldValue name="integrationType">
          {(integrationType: IPCameraIntegrationType) => (
            <FormItemCol>
              <DefaultFormItem
                name="username"
                label="Username"
                rules={
                  integrationType === 'landwatch'
                    ? [{ required: true, message: 'Please enter a username' }]
                    : []
                }
              >
                <Input placeholder="Farmbot" />
              </DefaultFormItem>
            </FormItemCol>
          )}
        </FieldValue>

        <FormItemCol>
          <DefaultFormItem
            name="password"
            label="Password"
            rules={[
              {
                required: true,
                message: 'Please enter a password'
              }
            ]}
          >
            <Input.Password placeholder="Password" />
          </DefaultFormItem>
        </FormItemCol>

        <FieldValue name="integrationType">
          {(integrationType: IPCameraIntegrationType) => integrationType === 'landwatch' && (
            <>
              <FormItemCol>
                <DefaultFormItem
                  name="domain"
                  label="Domain"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter a domain'
                    }
                  ]}
                >
                  <Input placeholder="fb000xxx.ddns.net" />
                </DefaultFormItem>
              </FormItemCol>

              <FormItemCol>
                <DefaultFormItem
                  name="port"
                  label="Port Number"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter a port number'
                    },
                    {
                      type: 'number',
                      min: 1,
                      max: 65535,
                      message: 'Port number must be between 1 and 65535'
                    }
                  ]}
                >
                  <InputNumber
                    placeholder="8080"
                    min={1}
                    max={65535}
                    className="w-full"
                  />
                </DefaultFormItem>
              </FormItemCol>
            </>
          )}
        </FieldValue>

        <FormItemCol>
          <DefaultFormItem
            name="serialNumber"
            label="Serial Number"
            rules={[
              {
                required: true,
                message: 'Please enter a serial number'
              }
            ]}
          >
            <Input placeholder="Serial Number" />
          </DefaultFormItem>
        </FormItemCol>
      </Row>
    </>
  );
}

export default memo(IPCameraAddFormItems);
